import React, { useState } from 'react';
import { Box, Typography, Paper, FormControlLabel, Checkbox, Button, Tabs, Tab, Alert, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDebug } from '../contexts/DebugContext';
import { useOnboarding } from '../contexts/OnboardingContext';
import { useEligibilityCacheExport } from '../components/EligibilityCacheExporter';
import BulkMigrationManager from '../components/admin/BulkMigrationManager';
import IndividualMigrationManager from '../components/admin/IndividualMigrationManager';
import EligibilityCacheManager from '../components/admin/EligibilityCacheManager';
import { useEligibilityRefresh } from '../hooks/useEligibilityRefresh';
import EligibilityRefreshProgressPreview from '../components/EligibilityRefreshProgressPreview';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `admin-tab-${index}`,
    'aria-controls': `admin-tabpanel-${index}`,
  };
}

const Admin: React.FC = () => {
  const { showPrerequisitesDebug, setShowPrerequisitesDebug } = useDebug();
  const { setShowOnboarding } = useOnboarding();
  const { exportCache, isExporting } = useEligibilityCacheExport();
  const { startRefresh, isActive, progress, statusMessage } = useEligibilityRefresh();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [exportStatus, setExportStatus] = useState<{ success: boolean; message: string } | null>(null);
  const [showProgressPreview, setShowProgressPreview] = useState(false);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleAddEditSchools = () => {
    navigate('/admin/add-edit-schools');
  };

  const handleAddEditEvents = () => {
    navigate('/admin/add-edit-events');
  };

  const handleExportCache = async () => {
    setExportStatus(null);
    try {
      const result = await exportCache();
      if (result.success) {
        setExportStatus({ success: true, message: 'Eligibility cache exported successfully to Firebase!' });
      } else {
        setExportStatus({ success: false, message: `Export failed: ${result.error}` });
      }
    } catch (error) {
      setExportStatus({ success: false, message: `Export failed: ${error instanceof Error ? error.message : 'Unknown error'}` });
    }
  };

  const handleStartRefresh = async () => {
    try {
      await startRefresh();
    } catch (error) {
      console.error('Failed to start refresh:', error);
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 2 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          Admin Dashboard
        </Typography>
        
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="admin dashboard tabs">
            <Tab label="Add/Edit Content" {...a11yProps(0)} />
            <Tab label="Debug Tools" {...a11yProps(1)} />
            <Tab label="Eligibility Cache" {...a11yProps(2)} />
            <Tab label="Bulk Migration" {...a11yProps(3)} />
            <Tab label="Individual Migration" {...a11yProps(4)} />
          </Tabs>
        </Box>
        
        <TabPanel value={tabValue} index={0}>
          <Typography variant="h6" gutterBottom>
            Add/Edit Content
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddEditSchools}
            >
              Add/Edit Schools
            </Button>
            
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddEditEvents}
            >
              Add/Edit Events
            </Button>
          </Box>
        </TabPanel>
        
        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom>
            Debug Settings
          </Typography>
          
          <FormControlLabel
            control={
              <Checkbox
                checked={showPrerequisitesDebug}
                onChange={(e) => setShowPrerequisitesDebug(e.target.checked)}
                color="primary"
              />
            }
            label="Show Program Prerequisites Debug Info"
          />
          
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 4 }}>
            When checked, this will display detailed debug information about prerequisites data 
            that drives eligibility calculations on Program Profile pages. This information 
            will only be visible to admin users when this setting is enabled.
          </Typography>
          
          <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setShowOnboarding(true)}
            >
              View Onboarding Modal Window
            </Button>
            
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setShowProgressPreview(!showProgressPreview)}
            >
              {showProgressPreview ? 'Hide' : 'Show'} Progress Indicator Preview
            </Button>
          </Box>
        </TabPanel>
        
        <TabPanel value={tabValue} index={2}>
          <EligibilityCacheManager />
        </TabPanel>
        
        <TabPanel value={tabValue} index={3}>
          <BulkMigrationManager />
        </TabPanel>
        
        <TabPanel value={tabValue} index={4}>
          <IndividualMigrationManager />
        </TabPanel>
        
      </Paper>
      
      {/* Progress Indicator Preview */}
      <EligibilityRefreshProgressPreview 
        open={showProgressPreview} 
        onClose={() => setShowProgressPreview(false)} 
      />
    </Box>
  );
};

export default Admin;
